import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { PartnerRequestModelComponent } from "./partner-request-model/partner-request-model.component";
import { finalize, Observable } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { PartnerViewComponent } from "./partner-view/partner-view.component";
import { PartnerService } from "../../../Utils/_services/admin/partners/partner.service";
import {
  IPartner,
  IPartnerApiData,
  IPartnerListReq,
  PartnerStatus,
} from "../../../Utils/_models/partner.model";
import { IAppResponse } from "../../../Utils/_interceptors/network-error.model";
import { SelectModule } from "primeng/select";
@Component({
  selector: "app-partners",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PartnerRequestModelComponent,
    SearchBoxComponent,
    SpinnerLoaderComponent,
    PartnerViewComponent,
    SelectModule,
  ],
  templateUrl: "./partners.component.html",
  styleUrl: "./partners.component.css",
})
export class PartnersComponent implements OnInit {
  constructor(
    private partnerService: PartnerService,
    private toastrService: ToastrService
  ) {}

  loading = signal(false);
  isVisible = signal(false);
  isView = signal(false);
  searchTerm = signal<string | null>(null);
  currentPage = signal(1);
  itemsPerPage = signal(10);
  totalCount = signal(0);
  totalPages = signal(0);
  startIndex = signal(1);
  endIndex = signal(10);
  allSelected = signal(false);
  partners = signal<IPartner[]>([]);
  partnerType = signal<"Accept" | "Reject">("Accept");
  selectedId = signal<string | null>(null);
  selectedPartner = signal<IPartner | null>(null);
  partnerStatuses: { label: string; value: string }[] = [];
  selectedPartnerStatus: PartnerStatus | null = null;
  currentSortField = signal<
  "status" | "createdAt" | null
  >(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);
  pageSizes: number[] = [5, 10, 20, 50];
  pageSize = signal(0);
  ngOnInit(): void {
    this.pageSize.set(this.pageSizes[0]);

    this.partnerStatuses = Object.values(PartnerStatus).map(l => ({
      label: l,
      value: l,
    }));
    this.fetchPartners();
    this.updatePagination();
  }

  fetchPartners() {
    this.loading.set(true);
    this.selectedId.set(null);
    console.log("Fetching partners with status:", this.selectedPartnerStatus);
    const req: IPartnerListReq = {
      page: this.currentPage(),
      limit: this.pageSize(), 
      search: this.searchTerm(),
      status: this.selectedPartnerStatus,
      sort: this.currentSortField(),
      sortOrder: this.currentSortField() ? this.currentSortOrder() : null
    };

    this.partnerService
      .getAll(req)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: res => {
          this.partners.set(res.data.applications);
          this.totalCount.set(res.data.totalCount);
          this.updatePagination();
        },
        error: ex => {
          console.log(ex.message);
        },
      });
  }

  onSort(
    field: "status" | "createdAt" 
  ): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.fetchPartners();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  onStatusChange(event: { value: PartnerStatus | null }) {
    console.log("onStatusChange event:", event);
    this.selectedPartnerStatus = event?.value !== undefined ? event.value : (event as any);
    console.log("selectedPartnerStatus set to:", this.selectedPartnerStatus);
    this.currentPage.set(1);
    this.fetchPartners();
  }

  onSearch(value?: string) {
    this.currentPage.set(1);
    this.searchTerm.set(value ?? null);
    this.fetchPartners();
  }

  toggleSelectAll() {
    this.allSelected.set(!this.allSelected());
    this.partners().forEach(p => (p.selected = this.allSelected()));
  }

  updateSelectAllState() {
    const selectedCount = this.partners().filter(p => p.selected).length;
    this.allSelected.set(
      selectedCount === this.partners().length && this.partners().length > 0
    );
  }

  updatePagination() {
  if (this.totalCount() === 0) {
    this.startIndex.set(0);
    this.endIndex.set(0);
    this.totalPages.set(1);
    return;
  }

  this.totalPages.set(
    Math.ceil(this.totalCount() / this.pageSize())
  );

  this.startIndex.set((this.currentPage() - 1) * this.pageSize() + 1);
  this.endIndex.set(
    Math.min(this.currentPage() * this.pageSize(), this.totalCount())
  );
}
  previousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update(value => value - 1);
      this.updatePagination();
        this.fetchPartners();
    }
  }

  nextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update(value => value + 1);
      this.updatePagination();
        this.fetchPartners();
    }
  }

  Close() {
    this.isVisible.set(false);
  }

  view(partner: IPartner) {
    this.selectedPartner.set(partner);
    this.isView.set(true);
  }

  closeView() {
    this.selectedPartner.set(null);
    this.isView.set(false);
  }

  async accept(partner: IPartner) {
    this.partnerType.set("Accept");
    this.selectedId.set(partner._id);
    this.isVisible.set(true);
  }

  response(message: string) {
    if (this.selectedId() != null) {
      let obj: Observable<IAppResponse<IPartnerApiData>>;
      if (this.partnerType() === "Accept") {
        obj = this.partnerService.acceptRequest(this.selectedId()!, message);
      } else {
        obj = this.partnerService.rejectRequest(this.selectedId()!, message);
      }

      obj.subscribe({
        next: res => {
          if (res.auth.success) {
            this.fetchPartners();
            this.selectedId.set(null);
            this.isVisible.set(false);
          }
        },
        error: err => {
          this.toastrService.error(err.message);
        },
      });
    }
  }

  reject(partner: IPartner) {
    this.partnerType.set("Reject");
    this.selectedId.set(partner._id);
    this.isVisible.set(true);
  }

  canAccept(partner: IPartner): boolean {
    return partner.status === "pending";
  }

  canReject(partner: IPartner): boolean {
    return partner.status === "pending";
  }

  trackByPartnerId(index: number, p: IPartner) {
    return p._id;
  }

 getDisplayName(p: IPartner): string {
  const name = p.user?.name?.trim();
  return name || 'N/A';
}

getEmail(p: IPartner): string {
  return p.user?.email?.trim() || 'N/A';
}

 onPageSizeChange(newSize: number) {
    this.pageSize.set(newSize);
    this.currentPage.set(1);
    this.fetchPartners();
  }
}
